import React, { useEffect, useState } from "react";
import styles from "./styles.module.scss";
import LinkSquare from "../../../public/images/link-square.svg";
import ViewProgramCard from "../../components/Programs/ViewProgramCard";
import { schedulingFetchGet } from "../../utils/request/scheduling/fetch";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ProgramState } from "app/reducers/scheduling/program";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { RootState } from "typedefs";
import UserModel from "app/models/UserModel";
import useCurrentFacility from "app/utils/hooks/scheduling/useCurrentFacility";
import SuccessRegistrationModal from "app/components/Programs/SuccessRegistrationModal";
import WaitlistConfirmationModal from "app/components/Programs/WaitlistConfirmationModal";

interface Params {
  program_id: string;
}

const ViewPrograms = ({ isPublic = false }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const { program_id } = useParams<Params>();
  const [program, setProgram] = useState<ProgramState>();
  const userState = useSelector(
    (state: RootState): UserModel => state.user.data,
  );
  const [refetch, setRefetch] = useState(true);
  const userType = userState?.kind;
  const currentFacility = useCurrentFacility();
  const facilityId = currentFacility?.data?.id;
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showWaitlistModal, setShowWaitlistModal] = useState(false);

  // Reload when the route's program changes while the component stays mounted
  // (client-side navigation between program pages reuses this instance).
  useEffect(() => {
    setRefetch(true);
  }, [program_id]);

  useEffect(() => {
    if (!refetch) {
      return;
    }

    // A logged-in athlete who isn't synced into the scheduling service yet gets
    // a 401 on every private endpoint, including this one. Fall back to the
    // public program (it carries the coach external id) so the page renders and
    // the contact can be synced before any private call runs. Only 401 triggers
    // the fallback — other errors bounce as before so real outages aren't
    // masked with a stripped-down public view.
    const loadProgram = async () => {
      try {
        const result = await schedulingFetchGet(`/programs/${program_id}`, {}, {}, null, isPublic);
        setProgram(result);
      } catch (error) {
        if (!isPublic && error.status === 401) {
          try {
            const publicResult = await schedulingFetchGet(`/programs/${program_id}`, {}, {}, null, true);
            setProgram(publicResult);
          } catch (publicError) {
            history.push("/programs");
            toast.error(publicError.errors);
          }
        } else {
          history.push("/programs");
          toast.error(error.errors);
        }
      } finally {
        setRefetch(false);
      }
    };

    loadProgram();
  }, [refetch]);

  // Check for registration success query param
  useEffect(() => {
    const params = new URLSearchParams(location.search);

    if (params.get("registration_success") === "true") {
      setShowSuccessModal(true);
      // Clear the query param
      params.delete("registration_success");
      const newSearch = params.toString();
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      history.replace(newPath);
    }
    if (params.get("waitlist") === "true") {
      setShowWaitlistModal(true);
      // Clear the query param
      params.delete("waitlist");
      const newSearch = params.toString();
      const newPath = newSearch
        ? `${location.pathname}?${newSearch}`
        : location.pathname;
      history.replace(newPath);
    }
  }, [location.search, location.pathname, history]);

  const [tooltipText, setTooltipText] = useState("Copy URL");
  let url = window.location.href;

  const handleButtonClick = () => {
    const privacy = program?.privacy;
    if (privacy === "public" && !url.includes("/public")) {
      url = url.replace("/programs", "/public/programs");
    }
    navigator.clipboard.writeText(url);

    setTooltipText(t("URL copied to clipboard"));

    setTimeout(() => {
      setTooltipText(t("Copy URL"));
    }, 2000);
  };

  return (
    <div
      className={`programs-page-container ${url.includes("/public") ? styles.public_container : styles.container}`}
    >
      <div className={styles.imageContainer}>
        <img src={program?.externalHeaderUrl} />
        {userType === "coach" && facilityId === program?.facilityId && (
          <div className={styles.getLinkButton} onClick={handleButtonClick}>
            <span className={styles.iconWrapper}>
              <img src={LinkSquare} />
            </span>
            <div className={styles.tooltip_container}>
              <span className={styles.tooltip_button}>{t("Get Link")}</span>
              <span className={styles.tooltip}>{tooltipText}</span>
            </div>
          </div>
        )}
      </div>
      <div className={styles.container}>
        <ViewProgramCard
          setRefetch={setRefetch}
          program={program}
          programId={program_id}
          isPublic={url.includes("/public")}
        />
      </div>
      <SuccessRegistrationModal
        isOpen={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
      />
      <WaitlistConfirmationModal
        isOpen={showWaitlistModal}
        onClose={() => setShowWaitlistModal(false)}
      />
    </div>
  );
};

export default ViewPrograms;
